import type { ReactNode } from 'react';
import './CorniceDamiera.css';

interface CorniceDamieraProps {
  /** La damiera: un `<svg>` nudo, o un div che lo contiene. */
  children: ReactNode;
  /**
   * Classi dell'host, sulla scatola ESTERNA. E' li' che vive il layout — tetto,
   * rapporto d'aspetto, ancoraggio degli overlay — perche' quello lo decide la
   * schermata, non la cornice. La partita ci passa `gs-board-wrapper`.
   */
  className?: string;
  /**
   * Classi dell'host sul riquadro INTERNO. Serve alla partita, che tiene li'
   * `gs-board-frame`: quel selettore non e' decorativo — `game-screen-360.test.tsx`
   * verifica l'annidamento `.gs-middle > .gs-board-wrapper > .gs-board-frame`, e
   * `scripts/giro-damiera.mjs` misura la damiera attraverso
   * `.gs-board-frame > .game-board-container > svg`. Due ragioni per non farlo
   * sparire in un refactoring.
   */
  classeInterna?: string;
  /**
   * La misura ridotta, per le anteprime piccole. Vedi l'avvertenza su `.cd--mini`
   * in CorniceDamiera.css: non si combina con un tetto che aggiunge
   * `var(--cornice-damiera)`.
   */
  mini?: boolean;
  /**
   * Spegne l'ombra esterna, per quando la cornice sta dentro una scatola che
   * ritaglia (`overflow: hidden`) e la taglierebbe a meta'.
   */
  piatta?: boolean;
  /**
   * Quel che si appoggia SOPRA la damiera restando dentro la cornice: fratello del
   * riquadro interno, non figlio. La partita ci manda i suoi banner, che sono in
   * `position: absolute` ancorati alla scatola esterna — il rettangolo della
   * damiera, non quello del vano che la contiene.
   */
  overlay?: ReactNode;
  /**
   * Quel che sta a FIANCO della cornice, non sopra la damiera: fratello del riquadro
   * interno come `overlay`, ma per chi si ancora FUORI dal rettangolo del legno
   * (`right: calc(100% + ...)`, `left: 100%`). La partita ci mette il regolo `+`/`-`,
   * che deve stare sullo spigolo della damiera a ogni misura e non dentro il campo
   * di gioco.
   *
   * Separato da `overlay` e non impilato dentro di lui perche' le due cose non si
   * leggono allo stesso modo: `overlay` e' «si appoggia sopra», e un giorno potrebbe
   * prendere un ritaglio o un `pointer-events` che a questo romperebbero il clic.
   */
  fianco?: ReactNode;
}

/**
 * La cornice di legno attorno alla damiera, uguale su ogni schermata.
 *
 * Perche' un componente e non due classi da scrivere a mano in ogni host: la
 * CONSEGNA del CSS. In questo pacchetto un foglio raggiunge una schermata solo se il
 * chunk di quella schermata lo importa, e la cosa ha gia' prodotto difetti —
 * `DashboardPage.css` viaggia solo col chunk pigro della dashboard (per questo
 * `DailyPuzzleInvitePage.css` si e' dovuta scrivere un prefisso tutto suo), e
 * `GameBoard.css` non e' caricato sul replay (per questo `GameReplay.css` ricopia a
 * mano i colori delle caselle, con un commento che ammette la sincronizzazione
 * manuale). Un componente che importa il proprio foglio porta il CSS con se' in ogni
 * chunk che lo usa, e la domanda «ma questo foglio ci arriva?» non si pone piu'.
 *
 * Il disegno e le misure stanno in CorniceDamiera.css e in `index.css`
 * (`--cornice-*`). Qui c'e' solo la struttura, che e' l'altra meta' del contratto:
 * ESATTAMENTE due div annidati. Chi tocca l'annidamento tocca anche
 * `game-screen-360.test.tsx`, che lo verifica.
 */
export default function CorniceDamiera({
  children,
  className,
  classeInterna,
  mini = false,
  piatta = false,
  overlay,
  fianco,
}: CorniceDamieraProps) {
  const classi = ['cd'];
  if (mini) classi.push('cd--mini');
  if (piatta) classi.push('cd--piatta');
  if (className) classi.push(className);

  return (
    <div className={classi.join(' ')}>
      <div className={classeInterna ? `cd-in ${classeInterna}` : 'cd-in'}>{children}</div>
      {overlay}
      {fianco}
    </div>
  );
}
